import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { CheckCircle, Phone, Shield, Clock, Award } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_landscape-pros-12/artifacts/pgiiqdu2_Untitled%20design%20%281%29.png";

export const AboutPage = () => {
  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-heading text-gray-900 mb-6">
                About <span className="text-primary">Elite Blade</span>
              </h1>
              <p className="text-gray-700 text-lg mb-6">
                Elite Blade Landscaping & Grounds Care was founded with a simple mission: 
                to elevate every property we touch. Based in Houston, Texas, we've built 
                our reputation on quality workmanship, reliability, and exceptional customer service.
              </p>
              <p className="text-gray-600 mb-8">
                Owner Damion brings years of experience and a passion for creating beautiful 
                outdoor spaces. Whether it's a small residential lawn or a large commercial 
                property, we treat every job with the same level of care and attention to detail.
              </p>
              <div className="flex items-center gap-4">
                <img src={LOGO_URL} alt="Elite Blade" className="h-16 w-auto" />
                <div>
                  <p className="text-gray-900 font-semibold">Damion</p>
                  <p className="text-gray-500 text-sm">Owner & Operator</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-lg overflow-hidden border border-gray-200 shadow-lg">
                <img 
                  src="https://images.unsplash.com/photo-1558904541-efa843a96f01?auto=format&fit=crop&w=800&q=80" 
                  alt="Beautiful Landscape"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-primary text-white p-6 rounded-lg shadow-lg">
                <p className="font-heading text-3xl">100%</p>
                <p className="font-accent text-sm">Satisfaction</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section data-testid="values-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-heading text-gray-900 mb-4">
              Our <span className="text-accent">Values</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              These core principles guide everything we do at Elite Blade.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: "Insured & Professional",
                description: "Fully insured for your peace of mind. We take our responsibility seriously and operate with the highest professional standards."
              },
              {
                icon: Clock,
                title: "Reliable & Consistent",
                description: "We show up when we say we will. Our clients can count on consistent, quality service every single time."
              },
              {
                icon: Award,
                title: "Quality Guaranteed",
                description: "We're not satisfied until you are. Our work speaks for itself, and we stand behind every job we complete."
              }
            ].map((value, index) => (
              <Card 
                key={index}
                data-testid={`value-${index}`}
                className="bg-gray-50 border-gray-200 hover:border-primary hover:shadow-lg transition-all"
              >
                <CardContent className="p-8 text-center">
                  <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                    <value.icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-heading text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section data-testid="why-us-section" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                <div className="aspect-square rounded-lg overflow-hidden border border-gray-200 shadow-md">
                  <img 
                    src="https://images.unsplash.com/photo-1630311853242-edd0c07766c0?auto=format&fit=crop&w=400&q=80" 
                    alt="Lawn Mowing"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square rounded-lg overflow-hidden border border-gray-200 shadow-md mt-8">
                  <img 
                    src="https://images.unsplash.com/photo-1631752674874-9994662712af?auto=format&fit=crop&w=400&q=80" 
                    alt="Garden Design"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square rounded-lg overflow-hidden border border-gray-200 shadow-md -mt-8">
                  <img 
                    src="https://images.unsplash.com/photo-1544914379-806667cd9489?auto=format&fit=crop&w=400&q=80" 
                    alt="Green Grass"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square rounded-lg overflow-hidden border border-gray-200 shadow-md">
                  <img 
                    src="https://images.unsplash.com/photo-1657383543451-e47d1589195d?auto=format&fit=crop&w=400&q=80" 
                    alt="Outdoor Space"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-4xl md:text-5xl font-heading text-gray-900 mb-6">
                Why <span className="text-primary">Choose Us?</span>
              </h2>
              <p className="text-gray-600 mb-8">
                When you choose Elite Blade, you're choosing a partner who cares about 
                your property as much as you do. Here's what sets us apart:
              </p>
              <ul className="space-y-4">
                {[
                  "Local Houston business - we know the area and its unique landscaping needs",
                  "Personalized service - you'll work directly with Damion, not a call center",
                  "Flexible scheduling - we work around your schedule",
                  "Transparent pricing - no hidden fees or surprise charges",
                  "Quality equipment - professional-grade tools for the best results",
                  "Satisfaction guaranteed - we're not done until you're happy"
                ].map((item, index) => (
                  <li key={index} className="flex items-start text-gray-700">
                    <CheckCircle className="h-5 w-5 text-primary mr-3 mt-1 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section data-testid="stats-section" className="py-24 bg-primary">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: "5+", label: "Years Experience" },
              { number: "500+", label: "Happy Customers" },
              { number: "100%", label: "Satisfaction Rate" },
              { number: "24/7", label: "Support Available" }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-heading text-white mb-2">{stat.number}</div>
                <p className="text-white/80 font-accent text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-heading text-gray-900 mb-6">
            Ready to Work With Us?
          </h2>
          <p className="text-gray-600 mb-8">
            Let's discuss how we can help transform your property. Contact us today 
            for a free consultation and quote.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button 
                data-testid="about-cta-btn"
                size="lg"
                className="bg-accent hover:bg-accent/90 text-black font-accent text-lg px-8"
              >
                Get in Touch
              </Button>
            </Link>
            <a href="tel:7134999372">
              <Button 
                data-testid="about-call-btn"
                size="lg"
                variant="outline"
                className="border-primary text-primary hover:bg-primary hover:text-white font-accent text-lg px-8"
              >
                <Phone className="mr-2 h-5 w-5" />
                (713) 499-9372
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
